/**
 * NewUser Modal Component
 *
 * Modal for creating a new user.
 * Features: form validation, role assignment, member type selection, notification.
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconMail } from '@tabler/icons-react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Grid,
  Alert,
  TextInput,
  Select,
  MultiSelect,
  Switch,
  Text,
  Checkbox,
} from '../../../components/ui';
import { toast } from '../../../components/ui';
import { useMemberTypeService, useMaritalStatusService } from '@actschurch/ccms-sdk';
import { useUserManagement } from './hooks/useUserManagement';
import type { NewUserModalProps, CreateUserViewModel } from './types';
import type { MemberType, MaritalStatus } from '@actschurch/ccms-sdk';

export function NewUser({ opened, onClose, onSuccess, availableRoles }: NewUserModalProps) {
  const userManagement = useUserManagement();
  const memberTypeService = useMemberTypeService();
  const maritalStatusService = useMaritalStatusService();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([]);

  // Fetch member types and marital statuses
  useEffect(() => {
    if (!opened) return;
    const fetchData = async () => {
      try {
        const [types, statuses] = await Promise.all([
          memberTypeService.getAll(),
          maritalStatusService.getAll(),
        ]);
        setMemberTypes(types);
        setMaritalStatuses(statuses);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Form setup with validation
  const form = useForm({
    initialValues: {
      email: '',
      firstName: '',
      surname: '',
      cellphone: '',
      gender: '',
      isMember: true,
      maritalStatusID: 1,
      typeId: 1,
      roles: [] as string[],
      sendNotification: true,
    },
    validate: {
      email: (value) => {
        if (!value || value.trim() === '') return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      firstName: (value) => {
        if (!value || value.trim() === '') return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        return null;
      },
      surname: (value) => {
        if (!value || value.trim() === '') return 'Surname is required';
        if (value.trim().length < 2) return 'Surname must be at least 2 characters';
        return null;
      },
      cellphone: (value) => {
        if (value && value.trim() !== '') {
          if (!/^[\d\s\-\+\(\)]+$/.test(value)) {
            return 'Invalid phone number format';
          }
        }
        return null;
      },
    },
  });

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ];

  const memberTypeOptions = useMemo(
    () => memberTypes.map((type) => ({ value: String(type.typeID), label: type.name })),
    [memberTypes]
  );

  const maritalStatusOptions = useMemo(
    () =>
      maritalStatuses.length > 0
        ? maritalStatuses.map((s) => ({ value: String(s.maritalStatusID), label: s.name }))
        : [
            { value: '1', label: 'Single' },
            { value: '2', label: 'Married' },
            { value: '3', label: 'Divorced' },
            { value: '4', label: 'Widowed' },
          ],
    [maritalStatuses]
  );

  const roleOptions = availableRoles.map((role) => ({
    value: role.name!,
    label: role.name!,
  }));

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: typeof form.values) => {
    setSubmitError(null);

    try {
      const userData: CreateUserViewModel = {
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        surname: values.surname.trim(),
        cellphone: values.cellphone.trim() || null,
        gender: values.gender || null,
        isMember: values.isMember,
        maritalStatusID: parseInt(String(values.maritalStatusID)),
        roles: values.roles.length > 0 ? values.roles : null,
        typeId: parseInt(String(values.typeId)),
        locationId: 0,
        sendNotification: values.sendNotification,
      };

      await toast.promise(userManagement.createUser(userData), {
        loading: 'Creating new user...',
        success: values.sendNotification
          ? 'User created successfully. Notification sent.'
          : 'User created successfully',
        error: (err: any) => err.message || 'Failed to create user',
      });

      form.reset();
      onSuccess();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to create user');
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    form.reset();
    setSubmitError(null);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create New User" size="lg" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Error Alert */}
          {submitError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          {/* Email (Required) */}
          <TextInput
            label="Email Address"
            placeholder="user@example.com"
            description="This will be the username for login"
            required
            {...form.getInputProps('email')}
            disabled={userManagement.loading}
          />

          {/* First Name & Surname */}
          <Grid gutter="md">
            <Grid.Col span={6}>
              <TextInput
                label="First Name"
                placeholder="John"
                required
                {...form.getInputProps('firstName')}
                disabled={userManagement.loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Surname"
                placeholder="Doe"
                required
                {...form.getInputProps('surname')}
                disabled={userManagement.loading}
              />
            </Grid.Col>
          </Grid>

          {/* Cellphone */}
          <TextInput
            label="Cellphone"
            placeholder="+27 123 456 7890"
            {...form.getInputProps('cellphone')}
            disabled={userManagement.loading}
          />

          {/* Gender & Marital Status */}
          <Grid gutter="md">
            <Grid.Col span={6}>
              <Select
                label="Gender"
                placeholder="Select gender"
                data={genderOptions}
                {...form.getInputProps('gender')}
                disabled={userManagement.loading}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Marital Status"
                placeholder="Select status"
                data={maritalStatusOptions}
                {...form.getInputProps('maritalStatusID')}
                value={String(form.values.maritalStatusID)}
                onChange={(value) => form.setFieldValue('maritalStatusID', Number(value) || 1)}
                disabled={userManagement.loading}
              />
            </Grid.Col>
          </Grid>

          {/* Member Type */}
          <Select
            label="Member Type"
            placeholder="Select member type"
            data={memberTypeOptions}
            {...form.getInputProps('typeId')}
            value={String(form.values.typeId)}
            onChange={(value) => form.setFieldValue('typeId', Number(value) || 1)}
            disabled={userManagement.loading}
          />

          {/* Roles */}
          <MultiSelect
            label="Roles"
            placeholder="Select roles"
            data={roleOptions}
            {...form.getInputProps('roles')}
            disabled={userManagement.loading}
            searchable
            clearable
          />

          {/* Is Member Switch */}
          <Switch
            label="Is Member"
            description="Mark this user as a church member"
            {...form.getInputProps('isMember', { type: 'checkbox' })}
            disabled={userManagement.loading}
          />

          {/* Send Notification */}
          <Checkbox
            label="Send notification to user"
            description="Email the user their login credentials (username & temporary password)"
            {...form.getInputProps('sendNotification', { type: 'checkbox' })}
            disabled={userManagement.loading}
          />

          {form.values.sendNotification && (
            <Alert color="blue" icon={<IconMail size={16} />}>
              <Text size="sm">
                The user will receive an email with their login details. Temporary password:{' '}
                <Text component="span" fw={600}>
                  {form.values.firstName.trim() || 'User'}@1234
                </Text>
              </Text>
            </Alert>
          )}

          {/* Form Actions */}
          <Group justify="flex-end" gap="sm" mt="md">
            <Button variant="light" onClick={handleClose} disabled={userManagement.loading}>
              Cancel
            </Button>
            <Button type="submit" loading={userManagement.loading} gradientVariant="primary">
              Create User
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
